import type {
  ApiDeepRequiredResponseBody,
  ApiResponseBody,
} from "@/lib/api/api-type-helper";
import type { EnumUnion } from "@dresscode-frontend/utility-types";
import type { ReactElement } from "react";
import type { UploadedFormDataFileInfo } from "../components/fields/FileField";

// APIレスポンスの型（主要フィールドを必須化）
type SelfServiceTaskFormResRequired = ApiDeepRequiredResponseBody<
  "/v2/self-service/tasks/{id}/forms/{operationTaskId}",
  "get"
>;

// APIレスポンスの型（オプショナルのまま）
type SelfServiceTaskFormResOptional = ApiResponseBody<
  "/v2/self-service/tasks/{id}/forms/{operationTaskId}",
  "get"
>;

// statusを除外し、approvalInfoのみオプショナルの型を使用
export type SelfServiceTaskForm = Omit<
  SelfServiceTaskFormResRequired,
  "status" | "approvalInfo" | "todoTaskInfo"
> & {
  approvalInfo?: SelfServiceTaskFormResOptional["approvalInfo"];
  todoTaskInfo?: SelfServiceTaskFormResOptional["todoTaskInfo"];
};
type AttributeForm = SelfServiceTaskForm["form"];

type AttributeCollections = AttributeForm["attributeCollections"];

export type AttributeCollection = AttributeCollections[number] & {
  /**
   * コレクションのレイアウトスタイル
   * - vertical: ラベル・説明文とフィールドを縦に並べる（デフォルト）
   * - horizontal: ラベル・説明文とフィールドを横に並べる
   */
  layout?: "vertical" | "horizontal";
};
export type AttributeCollectionId = AttributeCollections[number]["id"];

export type AttributeGroups = AttributeCollection["attributeGroupSettings"];
export type AttributeGroup = AttributeGroups[number];
export type AttributeGroupId = AttributeGroup["id"];

type Attributes = AttributeGroup["attributes"];
export type Attribute = Attributes[number] & {
  /**
   * フィールドのレイアウトスタイル
   * - vertical: ラベル・説明文とフィールドを縦に並べる（デフォルト）
   * - horizontal: ラベル・説明文とフィールドを横に並べる
   */
  layout?: "vertical" | "horizontal";
};
export type AttributeId = Attribute["id"];

export type ValidationRules = NonNullable<Attribute["validationRules"]>;
export type ValidationRule = NonNullable<ValidationRules>[number];

export type SelectionItems = Attribute["selectionItems"];

export type DivisionItems = Attribute["divisionItems"];
export type DivisionItem = Required<NonNullable<DivisionItems>>;

export type FileType = Attribute["file"];

// ---------------------------------------------
// フィールド型定義
// ---------------------------------------------
export const FieldType = {
  Text: "Text",
  TextArea: "TextArea",
  Email: "Email",
  Tel: "Tel",
  Password: "Password",
  Url: "Url",
  HighlyConfidentialText: "HighlyConfidentialText",
  Integer: "Integer",
  Decimal: "Decimal",
  Date: "Date",
  Boolean: "Boolean",
  ChangeBoolean: "ChangeBoolean",
  SingleSelect: "SingleSelect",
  MultiSelectCheckbox: "MultiSelectCheckbox",
  Radio: "Radio",
  Hidden: "Hidden",
  Person: "Person",
  File: "File",
  ChangeText: "ChangeText",
  ChangeSingleSelect: "ChangeSingleSelect",
  ChangeInteger: "ChangeInteger",
  ChangeDecimal: "ChangeDecimal",
  ChangeRadio: "ChangeRadio",
} as const satisfies Record<string, Attribute["type"]>;
export type FieldType = EnumUnion<typeof FieldType>;

// ---------------------------------------------
// フィールドバリデーションルール
// ---------------------------------------------
// requiredはフィールドの必須属性として別管理
export const ValidationRuleType = {
  MinLength: "minLength",
  MaxLength: "maxLength",
  Regexp: "regexp",
  MinValue: "minValue",
  MaxValue: "maxValue",
  AcceptedExtension: "acceptedExtension",
} as const satisfies Record<string, ValidationRules[number]["rule"]>;
export type ValidationRuleType = EnumUnion<typeof ValidationRuleType>;

/**
 * フォームバリデーションのエラー型
 */
export interface FieldValidationError {
  fieldId: FieldId;
  message: string;
}

// ---------------------------------------------
// フォーム送信関連の型定義
// ---------------------------------------------

/**
 * フォーム送信属性値
 */
export interface FieldValue {
  /**
   * フィールドID
   * ${collectionId}.${collectionIndex}.${groupId}.${groupIndex}.${attributeId}
   */
  fieldId: FieldId;

  /**
   * 属性ID
   */
  attributeId: AttributeId;

  /**
   * フィールドの値
   */
  value: FieldValueType;
}

type TypedString<T extends string> = string & { _type: T };
/**
 * フィールドIDの型エイリアス
 * フォーマット: `${collectionId}.${collectionIndex}.${groupId}.${groupIndex}.${attributeId}`
 */
export type FieldId = TypedString<"fieldId">;

/**
 * 複数選択の値の型
 */
export type MultiSelectFieldValue = string[];

/**
 * フィールドの直接値型（BaseFieldやフィールドコンポーネント間でやり取りされる値の型）
 */
export type FieldValueType =
  | string
  | number
  | boolean
  | Date
  | undefined
  | Record<string, unknown>
  | Array<Record<string, unknown>>
  | MultiSelectFieldValue
  | (File | UploadedFormDataFileInfo)[];

/**
 * 共通のフィールドプロパティ
 */
export interface SharedFieldProps {
  /** フィールドID */
  fieldId: FieldId;

  /** フィールドのレンダリング関数 */
  renderInput?: FieldRenderInputFunction;

  /** 読み取り専用モード */
  readOnly?: boolean;

  /** 無効化モード */
  disabled?: boolean;

  /** レイアウト（縦方向または横方向） */
  layout?: "vertical" | "horizontal";

  /** CSSクラス */
  className?: string;

  /** テスト用ID */
  "data-testid"?: string;
}

export interface FieldRenderInputProps {
  id: string;
  name?: string;
  value: FieldValueType | undefined;
  previousValue: FieldValueType | undefined;
  onChange: (value: FieldValueType) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  error?: string | null;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  className?: string;
  "data-testid"?: string;
}

type FieldRenderInputFunction = (props: FieldRenderInputProps) => ReactElement;
